import { useEffect, useState } from "react";
import { motion } from "framer-motion";

// ScrollProgress: A thin progress bar at the top that indicates scroll position
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", update);
    return () => window.removeEventListener("scroll", update);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// Carte section animée et stylisée
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true }}
    className="flex-1 p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-blue-100 rounded"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Paragraphe stylisé
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-base text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

// Page stylisée à l'identique de UcWavesRoadmap
const BaseL2 = () => (
  <div className="relative overflow-hidden space-y-8 px-4 py-10 md:py-6 md:px-8">
    <ScrollProgress />

    {/* Titre et intro animée */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: "easeOut",
        type: "spring",
        stiffness: 200,
        damping: 20,
      }}
      viewport={{ once: true }}
      className="max-w-6xl mx-auto space-y-4 text-center"
    >
      <Paragraph className="text-gray-700 max-w-6xl mx-auto mb-10">
        In the era of decentralized finance (DeFi), decentralized applications
        (dApps), and community-driven governance, choosing the right blockchain
        infrastructure is paramount. For Uncensored Waves (UcWaves), which
        empowers users to voice opinions on pressing topics, we selected Base
        Layer 2 (L2) as our deployment platform.
      </Paragraph>
    </motion.div>

    {/* Colonnes animées */}
    <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
      <SectionCard title="1. Security Inherited from Ethereum">
        <strong className="block mb-1 font-semibold text-base text-primary-dark">
          1.1 Ethereum’s Robust Proof-of-Stake Consensus:
        </strong>
        <Paragraph>
          Base L2 settles transactions on Ethereum’s proof-of-stake (PoS)
          consensus layer. By batching and posting proofs to the Ethereum
          mainnet, Base benefits from Ethereum’s robust security guarantees.
        </Paragraph>

        <strong className="block mb-1 font-semibold text-base text-primary-dark">
          1.2 Decentralized Validation:
        </strong>
        <Paragraph>
          Unlike some sidechains or centralized rollups, Base remains anchored
          to Ethereum through regular checkpointing. Any attempt to censor or
          alter transaction history would require compromising Ethereum’s
          security.
        </Paragraph>

        <strong className="block mb-1 font-semibold text-base text-primary-dark">
          1.3 Smart Contract Safety:
        </strong>
        <Paragraph>
          Base supports the EVM, enabling use of battle-tested frameworks and
          security tools like OpenZeppelin, Slither, and MythX.
        </Paragraph>
      </SectionCard>

      <SectionCard title="2. Economical Transaction Fees">
        <strong className="block mb-1 font-semibold text-base text-primary-dark">
          2.1 Gas Efficiency on L2:
        </strong>
        <Paragraph>
          Mainnet gas fees can be expensive during peak times. Base reduces
          these by executing transactions off-chain before settling on Ethereum.
        </Paragraph>

        <strong className="block mb-1 font-semibold text-base text-primary-dark">
          2.2 Predictable Cost Structure:
        </strong>
        <Paragraph>
          Base offers consistent low fees, critical for platforms involving
          microtransactions like tipping or voting.
        </Paragraph>

        <strong className="block mb-1 font-semibold text-base text-primary-dark">
          2.3 Sustainable Economics:
        </strong>
        <Paragraph>
          UcWaves can design incentive mechanisms for contributors without
          losing value to gas costs.
        </Paragraph>
      </SectionCard>
    </div>
  </div>
);

export default BaseL2;
